import { Formik } from 'formik';
import _ from 'lodash';
import { getSignUpErrorMessage } from 'modules/auth/domain/constants/sign-up-error-messages';
import { AuthSignUpPayload } from 'modules/auth/domain/interfaces/auth-sign-up-payload';
import { authSignUpValidator } from 'modules/auth/domain/validators/auth-sign-up.validator';
import { authSignIn, authSignUp } from 'modules/auth/store/actions';
import {
  authStoreCheckingSelector,
  authStoreCodeRequestedSelector,
  authStoreCodeRequestingSelector,
  authStoreErrorsSelector,
  authStoreLoadingSelector,
} from 'modules/auth/store/selectors';
import { SPORT_TYPE_LABELS } from 'modules/core/domain/constants/sport-type-labels';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import { AuthSignInPayload } from 'modules/auth/domain/interfaces/auth-sign-in-payload';
import { authSignInValidator } from 'modules/auth/domain/validators/auth-sign-in.validator';
import SignInForm from '../components/sign-in-form';
import AuthLayout from '../layouts';

type SportTypeOption = SelectOption<SportType>;

type SignUpFormValues = {
  firstName: string;
  lastName: string;
  middleName?: string;
  phone: string;
  sportType: SportTypeOption | null;
  tournamentName: string;
};

const SignUpPage = () => {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const isSignInLoading = useSelector(authStoreLoadingSelector);
  const stateErrors = useSelector(authStoreErrorsSelector);
  const isAuthCodeRequested = useSelector(authStoreCodeRequestedSelector);
  const isAuthCodeRequesting = useSelector(authStoreCodeRequestingSelector);
  const isAuthChecking = useSelector(authStoreCheckingSelector);

  const [username, setUsername] = useState('');

  const sportTypeOptions = useMemo<SportTypeOption[]>(
    () =>
      Object.values(SportType).map((value) => ({
        value,
        label: _.capitalize(t(SPORT_TYPE_LABELS[value])),
      })),
    [t],
  );

  const errors = useMemo(() => {
    if (!stateErrors) {
      return {};
    }

    return Object.keys(stateErrors as { [key: string]: string }).reduce<Record<string, string>>((result, key) => {
      const serverMessage = String((stateErrors as Record<string, unknown>)[key]);
      const message = getSignUpErrorMessage(key, serverMessage);
      return { ...result, [key]: message ? t(message) : serverMessage };
    }, {});
  }, [stateErrors, t]);

  const onSignUp = useCallback(
    (values: SignUpFormValues) => {
      if (!values.sportType) {
        return;
      }
      const payload: AuthSignUpPayload = {
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName,
        phone: values.phone,
        sportType: values.sportType.value,
        tournamentName: values.tournamentName,
      };
      setUsername(payload.phone);
      dispatch(authSignUp(payload))
        .unwrap()
        .catch((rejected) => {
          toast.error(rejected?.message ?? t`Не удалось зарегистрироваться. Попробуйте позже.`);
        });
    },
    [dispatch, t],
  );

  const onSignIn = useCallback(
    (data: AuthSignInPayload) => {
      dispatch(authSignIn(data));
    },
    [dispatch],
  );

  return (
    <AuthLayout
      logoTo={Pages.SIGN_IN}
      showPrivacyPolicy
      formContainerProps={{ style: { padding: 30 } }}
      title={t`Регистрация организатора`}
      action={{
        title: t`Уже есть аккаунт?`,
        linkLabel: t`Войти`,
        link: Pages.SIGN_IN,
      }}
    >
      {isAuthCodeRequested || isSignInLoading || isAuthChecking ? (
        <SignInForm
          initialValues={{ username, password: '' }}
          onSubmit={onSignIn}
          isLoading={isSignInLoading || isAuthChecking}
          validationSchema={authSignInValidator()}
        />
      ) : (
        <Formik<SignUpFormValues>
          enableReinitialize={true}
          initialValues={{
            firstName: '',
            lastName: '',
            middleName: '',
            phone: '',
            sportType: null,
            tournamentName: '',
          }}
          initialErrors={errors}
          onSubmit={onSignUp}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={authSignUpValidator()}
        >
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit}>
              <UDFormInput
                name="lastName"
                placeholder={t`Фамилия`}
                containerProps={{ style: { marginBottom: 12 } }}
                errorVisible={false}
              />
              <UDFormInput
                name="firstName"
                placeholder={t`Имя`}
                containerProps={{ style: { marginBottom: 12 } }}
                errorVisible={false}
              />
              <UDFormInput
                name="middleName"
                placeholder={t`Отчество`}
                containerProps={{ style: { marginBottom: 12 } }}
                errorVisible={false}
              />
              <UDInputPhone
                name="phone"
                placeholder={t`Номер телефона`}
                containerProps={{ style: { marginBottom: 12 } }}
                value={formProps.values.phone}
                errorVisible={false}
              />
              <UDFormSelect
                name="sportType"
                placeholder={t`Вид спорта`}
                isMulti={false}
                options={sportTypeOptions}
                containerProps={{ style: { marginBottom: 12 } }}
              />
              <UDFormInput
                name="tournamentName"
                placeholder={t`Название турнира`}
                containerProps={{ style: { marginBottom: 18 } }}
                errorVisible={false}
              />
              <UDButton
                variant="primary"
                type="submit"
                className="w-full"
                disabled={!formProps.isValid || !formProps.dirty}
                loading={isAuthCodeRequesting}
              >
                {t`Создать аккаунт`}
              </UDButton>
            </form>
          )}
        </Formik>
      )}
    </AuthLayout>
  );
};

export default SignUpPage;
